/*
 * Welham & Co product page: the two things on it that have to be worked out
 * in the browser (session W3, 2026-09-24).
 *
 * <wc-delivery>  the delivery line (blocks/wc-product-delivery). Its dates are
 *                counted here and not in Liquid, because Liquid's `now` is the
 *                moment the page was CACHED, which can be hours or days before
 *                the moment it is read. Christmas lines are chosen here for
 *                the same reason.
 * <wc-scale>     the in-room scale (sections/wc-product-what-you-get): the
 *                chosen size drawn to scale above a sofa.
 *
 * The top of this file is pure (no DOM), so bin/test-wc-pdp.mjs can drive the
 * arithmetic under Node. The custom elements are at the bottom.
 */

/** A calendar date (no time, no zone) from "YYYY-MM-DD", or null. */
export function parseDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Midnight of the given moment's own calendar day, in the shopper's zone. */
export function dayOf(moment) {
  return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate());
}

export function addDays(day, n) {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + n);
}

/** n business days after `day`: Saturdays and Sundays are skipped, holidays are not known. */
export function addBusinessDays(day, n) {
  let d = day;
  let left = n;
  while (left > 0) {
    d = addDays(d, 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) left -= 1;
  }
  return d;
}

/**
 * When a portrait ordered on `today` arrives, if the sketch preview comes in
 * firstLookMin..firstLookMax days (the names predate 7 Oct) and is approved
 * that day, the first look of the painted artwork takes `sketch` more business
 * days (a switched line only; 0 otherwise), printing and framing take `fulfil`
 * business days, and transit takes transitMin..transitMax business days.
 * Returns { from, to } calendar days.
 */
export function arrivalWindow(today, { firstLookMin, firstLookMax, sketch = 0, fulfil, transitMin, transitMax }) {
  const start = dayOf(today);
  const extra = Number(sketch) || 0;
  const from = addBusinessDays(addDays(start, firstLookMin), extra + fulfil + transitMin);
  const to = addBusinessDays(addDays(start, firstLookMax), extra + fulfil + transitMax);
  return { from, to };
}

/*
 * Dates are written by hand, not by Intl. formatRange's punctuation belongs to
 * the browser's ICU version: one runtime writes "9–16 October", another
 * "9 – 16 October" with thin spaces (it failed CI on 2026-09-25 while passing
 * locally), so shoppers would see whichever their browser ships. The brand's
 * rule is an unspaced en dash for ranges ("5–7", voice-and-tone.md), and the
 * store publishes English only, so the month names are fixed here too.
 */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DASH = "\u2013"; // en dash
const monthFirst = (locale) => /-US$/i.test(String(locale || ""));

/** "October 9" (en-US), "9 October" (en-GB and every other market). */
export function formatDay(day, locale) {
  const month = MONTHS[day.getMonth()];
  return monthFirst(locale) ? `${month} ${day.getDate()}` : `${day.getDate()} ${month}`;
}

/**
 * "October 9–16" (en-US), "9–16 October" (en-GB); across months "October 30–
 * November 5" / "30 October–5 November"; one date when both are the same day.
 */
export function formatWindow(from, to, locale) {
  if (from.getTime() === to.getTime()) return formatDay(from, locale);
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    const month = MONTHS[from.getMonth()];
    const days = `${from.getDate()}${DASH}${to.getDate()}`;
    return monthFirst(locale) ? `${month} ${days}` : `${days} ${month}`;
  }
  return `${formatDay(from, locale)}${DASH}${formatDay(to, locale)}`;
}

/** "2026-10-09" for a calendar day, in the shopper's own zone (never UTC). */
export function isoDay(day) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/**
 * Which Christmas line shows on `today`: 'before' up to and including the
 * cut-off day, 'after' from the day after it until 25 December of the cut-off's
 * year, otherwise null (no cut-off, a cut-off that cannot be read, or
 * Christmas has passed).
 */
export function christmasState(today, cutoffIso) {
  const cutoff = parseDay(cutoffIso);
  if (!cutoff) return null;
  const day = dayOf(today);
  if (day.getTime() <= cutoff.getTime()) return "before";
  const christmas = new Date(cutoff.getFullYear(), 11, 25);
  if (day.getTime() <= christmas.getTime()) return "after";
  return null;
}

/**
 * A size option value as inches, width first: "12×16", "16 x 12", "24×36 in"
 * and "12 × 16 in (landscape)" all read; anything else is null.
 */
export function parseSize(value) {
  const m = /(\d+(?:\.\d+)?)\s*[×xX]\s*(\d+(?:\.\d+)?)/.exec(String(value || ""));
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!(w > 0 && h > 0) || w > 96 || h > 96) return null;
  return { w, h };
}

/** "12 × 16 in", and "· 30 × 41 cm" when the market shows both units. */
export function sizeLabel({ w, h }, units) {
  const inches = `${w} × ${h} in`;
  if (units !== "in+cm") return inches;
  const cm = (x) => Math.round(x * 2.54);
  return `${inches} · ${cm(w)} × ${cm(h)} cm`;
}

/* ------------------------------------------------------------- the browser */

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  class WcDelivery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      const locale = this.dataset.locale || "en-US";
      const today = new Date();

      const eta = this.querySelector("[data-wc-eta-text]");
      const etaTemplate = this.querySelector("template[data-wc-eta-template]");
      const num = (k) => Number(this.dataset[k]);
      if (eta && etaTemplate && this.dataset.transitMax) {
        const { from, to } = arrivalWindow(today, {
          firstLookMin: num("firstLookMin"),
          firstLookMax: num("firstLookMax"),
          sketch: Number(this.dataset.sketch) || 0,
          fulfil: num("fulfil"),
          transitMin: num("transitMin"),
          transitMax: num("transitMax"),
        });
        const text = (etaTemplate.content.textContent || "").trim();
        if (text.includes("[dates]")) {
          eta.textContent = text.replace("[dates]", formatWindow(from, to, locale));
          this.dataset.eta = `${isoDay(from)}/${isoDay(to)}`;
        }
      }

      const state = christmasState(today, this.dataset.cutoff);
      if (state) {
        const line = this.querySelector(`[data-wc-christmas="${state}"]`);
        if (line) {
          const template = line.querySelector("template[data-wc-christmas-template]");
          if (template) {
            const text = (template.content.textContent || "").trim();
            const cutoff = parseDay(this.dataset.cutoff);
            template.replaceWith(
              document.createTextNode(text.replace("[date]", cutoff ? formatDay(cutoff, locale) : "")),
            );
          }
          line.hidden = false;
          this.dataset.christmas = state;
        }
      }
    }
  }

  class WcScale extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      this.addEventListener("change", (event) => {
        const input = event.target;
        if (input instanceof HTMLInputElement && input.name === this.dataset.name) this.show(input.value);
      });
      // The size the page was rendered for, else the first one.
      const checked = this.querySelector("input:checked") || this.querySelector("input");
      if (checked) {
        checked.checked = true;
        this.show(checked.value, true);
      }
    }

    /** Draw the size: the frame group is scaled from its bottom edge, where it hangs. */
    show(value, instant = false) {
      const size = parseSize(value);
      const art = this.querySelector("[data-wc-scale-art]");
      const caption = this.querySelector("[data-wc-scale-caption]");
      if (!size || !art) return;
      const base = { w: Number(this.dataset.baseW), h: Number(this.dataset.baseH) };
      art.style.transition = instant || reduceMotion.matches ? "none" : "";
      art.style.transform = `scale(${size.w / base.w}, ${size.h / base.h})`;
      if (caption) caption.textContent = sizeLabel(size, this.dataset.units);
      this.dataset.size = `${size.w}x${size.h}`;
    }
  }

  if (!customElements.get("wc-delivery")) customElements.define("wc-delivery", WcDelivery);
  if (!customElements.get("wc-scale")) customElements.define("wc-scale", WcScale);
}
